import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Label, Button } from '@nooz-optics/nooz-components-lib';
import '../../../../../../styles/global.css';
import { Radio } from '../../../../../UI/Elements/Radio/radio';
import { ColorPickerProps } from '../../../../Shop/ShopItem/ColorPicker';
import {
  productWithLiveShopifyState,
  variantState,
} from '../../../../../../utils/recoil/product';
import {
  returnShopifyVariant,
  updateUrlParams,
  subHeaderScrollTrigger,
  tabButtonRight,
  tabsButtonLeft,
} from '../../../../../../utils/globals';
import { withSuspense } from '../../../../../../utils/hocs/withSuspense';
import { withWindowScroll } from '../../../../../../utils/hocs/withWindowScroll';
import { ScrollableTabs } from '../../../../../UI/Navigation/ScrollableTabs';

export const ProductMainColorRadio = withSuspense(
  ({
    variants,
    selectedColor,
    hasMarginBottom = true,
    hastMarginLeft = false,
    setHoveredVariant,
  }) => {
    const [variant, setVariant] = useRecoilState(variantState);
    const productWithLiveShopify = useRecoilValue(productWithLiveShopifyState);

    return variants?.map((v, index) => {
      const isFirst = index === 0;

      // Margin left for first radio (e.g. needed in ProductMaintSubHeader)
      const isFirstAndHasMarginLeft = isFirst && hastMarginLeft;

      const shopifyVariant =
        productWithLiveShopify &&
        returnShopifyVariant(productWithLiveShopify, v);

      // Check if at least one variant is available
      let isSelectedColorAvailable = true;

      if (shopifyVariant) {
        for (let i = 0; i < shopifyVariant.length; i++) {
          const item = shopifyVariant[i];
          if (item.availableForSale === true) {
            isSelectedColorAvailable = true;
            break;
          } else {
            isSelectedColorAvailable = false;
          }
        }
      }

      if (v.color) {
        const onColorChange = () => {
          // handle url param update
          updateUrlParams('color', v.color?.shopifyKey?.toLowerCase());
          // update recoil variant state
          setVariant({ ...v, diopter: variant?.diopter });
        };

        const color = v.color?.shopifyKey?.toLowerCase();

        return (
          <div
            onMouseEnter={() =>
              setHoveredVariant &&
              setHoveredVariant(v.color.text?.toLowerCase())
            }
            onMouseLeave={() => setHoveredVariant && setHoveredVariant(null)}
            className={`pr-4 ${hasMarginBottom ? 'pb-4' : ''} ${
              isFirstAndHasMarginLeft ? 'ml-6' : ''
            }`}
          >
            <Radio
              size='sm'
              isCrossed={!isSelectedColorAvailable}
              onClick={onColorChange}
              color={v.color.hex}
              checked={selectedColor?.shopifyKey?.toLowerCase() === color}
            />
          </div>
        );
      }
    });
  },
  <></>
);

export const ProductMainColorPicker: React.FC<ColorPickerProps> = withSuspense(
  ({ label, productSize, selectedColor, variants, hasBorder }) => {
    const [hoveredVariant, setHoveredVariant] = useState();
    const variant = useRecoilValue(variantState);

    let colorVariants = [...variants];

    // filter variant for product with size
    if (productSize && variant) {
      colorVariants = colorVariants.filter(
        (v) => productSize === v.size?.value
      );
    }

    const hasSelectedColor = colorVariants.find(
      (v) => selectedColor?.shopifyKey === v.color?.shopifyKey
    );

    return (
      <div className={`w-full ${hasBorder ? 'border-b' : ''}`}>
        <div className='flex my-3 items-center'>
          <Label
            weight='bold'
            color={{ classname: 'gray-700' }}
            size='base'
            text={label?.text}
          />
          &nbsp;&#45;&nbsp;
          {(hoveredVariant || hasSelectedColor) && (
            <Label
              size='base'
              transform='capitalize'
              weight='light'
              text={hoveredVariant || selectedColor?.text}
            />
          )}
        </div>
        <div className='flex flex-wrap'>
          <ProductMainColorRadio
            variants={colorVariants}
            selectedColor={selectedColor}
            setHoveredVariant={setHoveredVariant}
          />
        </div>
      </div>
    );
  },
  <></>
);

export const ProductMainColorPickerSlider: React.FC<ColorPickerProps> = withSuspense(
  withWindowScroll(({ scroll, productSize, selectedColor, variants }) => {
    const tabsButton = [
      <Button {...tabsButtonLeft} size='sm' />,
      <Button {...tabButtonRight} size='sm' />,
    ];

    const variant = useRecoilValue(variantState);
    // Trigger color picker arrow right on ScrollableTabs component when ProductMainSubHeader is active
    const isSubHeaderActive = scroll >= subHeaderScrollTrigger;

    let colorVariants = [...variants];

    // filter variant for product with size
    if (productSize && variant) {
      colorVariants = colorVariants.filter(
        (v) => productSize === v.size?.value
      );
    }

    return (
      <ScrollableTabs
        buttons={tabsButton}
        hasColorPicker
        shouldOverflow={isSubHeaderActive}
      >
        <ProductMainColorRadio
          hasMarginBottom={false}
          hastMarginLeft
          variants={colorVariants}
          selectedColor={selectedColor}
        />
      </ScrollableTabs>
    );
  }),
  () => <div></div>
);
